import { ChangeDetectionStrategy, Component, ElementRef, Injector, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonRow,
  IonTextarea,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IDecaDocument, UUID } from '@okcargo-apps/shared-domain';
import { DecaAddReservationForm } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-add-reservation-modal',
  templateUrl: './deca-add-reservation-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonTextarea,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    DecaListItemComponent,
  ],
})
export class DecaAddReservationModalComponent {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);

  protected readonly form: FormGroup<DecaAddReservationForm> = this.fb.group<DecaAddReservationForm>({
    reservationText: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(500)]),
  });

  protected get errorText(): string {
    const control = this.form.controls.reservationText;
    if (control.hasError('required')) return 'Debe añadir texto para poder enviar una anotación o reserva';
    return 'Texto no válido';
  }

  protected async saveChanges(): Promise<void> {
    const rawText = this.form.controls.reservationText.value.trim();

    if (this.form.invalid || !rawText) {
      if (!rawText) this.form.controls.reservationText.setValue('');
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const updatedDocument: IDecaDocument = {
        ...this.document,
        reservations: [...(this.document.reservations || []), rawText],
      };

      await this.decaMutationsService.addReservation(updatedDocument);

      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: ['Ha enviado una nueva anotación a este documento'],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error adding reservation', error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
